import { useMemo } from 'react'

import { ChartEntry } from './helpers'

interface ScaleResult {
  domain: number[]
  scale: (val: number) => number
}

/**
 * Computes axis domains and linear scale functions for the APY/TVL chart.
 * - xDomain / xScale: timestamp range → pixel X position
 * - yDomains / yScales: per-key value ranges (with 10% padding) → pixel Y position
 * Null values (from gap-filled data) are filtered out when computing Y domains.
 */
export const useChartScales = ({
  data,
  width,
  height,
  marginLeft = 60,
  marginRight = 60,
  yKeys,
}: {
  data: ChartEntry[]
  width: number
  height: number
  marginLeft?: number
  marginRight?: number
  yKeys: (keyof ChartEntry)[]
}) => {
  // Timestamps value [min, max]
  const xDomain = useMemo(() => {
    const timestamps = data.map(d => d.timestampMs)
    return [Math.min(...timestamps), Math.max(...timestamps)]
  }, [data])

  // X axis pixel domain
  const xRange = useMemo(() => [marginLeft, width - marginRight], [marginLeft, marginRight, width])

  // Y axis pixel domain
  const yRange = useMemo(() => [height, 0], [height])

  const createLinearScale = (domain: number[], range: number[]) => {
    const [dMin, dMax] = domain
    const [rMin, rMax] = range
    // Returns scaling function
    return (value: number) => rMin + ((value - dMin) * (rMax - rMin)) / (dMax - dMin)
  }

  const xScale = useMemo(() => createLinearScale(xDomain, xRange), [xDomain, xRange])

  const yScales = useMemo<Record<string, ScaleResult>>(
    () =>
      yKeys.reduce(
        (acc, key) => {
          const values = data.map(d => d[key] as number)
          const min = Math.min(...values)
          const max = Math.max(...values)
          const range = max - min
          const padding = range * 0.1 || 1 // fallback padding to avoid identical min/max
          const domain = [min - padding, max + padding]
          acc[key] = {
            domain,
            scale: createLinearScale(domain, yRange),
          }
          return acc
        },
        {} as Record<string, ScaleResult>
      ),
    [data, yKeys, yRange]
  )

  const yDomains = useMemo(() => {
    return Object.fromEntries(Object.entries(yScales).map(([k, v]) => [k, v.domain])) as Record<
      keyof ChartEntry,
      [number, number]
    >
  }, [yScales])

  return {
    xScale,
    yScales,
    xDomain,
    yDomains,
  }
}
